/**
 * [#1314] Cross-surface microphone mutex.
 *
 * Two independent voice surfaces exist in the LMC app and neither knew about
 * the other: the PiP session (Simurgh, an app-level singleton that survives
 * navigation) and the legacy voice page (Sphinx, a mounted `LiveKitRoom`).
 * Nothing stopped a client from starting one while the other was connected —
 * two agents, one microphone, both transcribing, both billing, and a
 * transcript split across two conversations with no marker saying why.
 *
 * The mutex is a module-level singleton because the thing being guarded is a
 * singleton: the browser tab has one microphone. It is deliberately NOT a
 * React hook and NOT in a store — a surface that has already connected must be
 * able to claim without re-rendering, and a surface tearing down must be able
 * to release from a cleanup function.
 *
 * Two rules:
 *   - `claim` is honest about failure: it returns false rather than stealing,
 *     so the CALLER decides what the person sees. Silently hanging up someone’s
 *     live call to start another is worse than refusing the second one.
 *   - `release` is owner-scoped: a surface can only release a claim it holds,
 *     so a late teardown from an already-superseded surface cannot free the
 *     mic out from under the surface that legitimately took it.
 */

/** The surfaces that can hold the microphone. */
export type VoiceOwner = 'pip' | 'legacy'

let holder: VoiceOwner | null = null

/**
 * Take the microphone for `owner`.
 *
 * Re-claiming while already holding it is a no-op success — a surface
 * restarting its own session must not deadlock against itself.
 *
 * @returns true when `owner` now holds the mic; false when another surface does.
 */
export function claimVoice(owner: VoiceOwner): boolean {
  if (holder !== null && holder !== owner) return false
  holder = owner
  return true
}

/** Release the mic, but only if `owner` is actually the one holding it. */
export function releaseVoice(owner: VoiceOwner): void {
  if (holder === owner) holder = null
}

/** Who holds the microphone right now, if anyone. */
export function voiceHolder(): VoiceOwner | null {
  return holder
}

/** True when a DIFFERENT surface holds the mic — the "refuse to start" test. */
export function voiceHeldByOther(owner: VoiceOwner): boolean {
  return holder !== null && holder !== owner
}

/**
 * Test-only. Never call from app code: forcing the mutex open is exactly the
 * steal the design refuses.
 */
export function __resetVoiceMutexForTests(): void {
  holder = null
}
